import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { ShieldCheck, Search, Filter, RefreshCcw, User, Clock, FileText, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function AuditoriaSystemPage() {
    const { toast } = useToast();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, per_page: 50, total: 0 });

    const fetchLogs = async (page = 1) => {
        setLoading(true);
        try {
            const response = await api.auditoria.list({ page });
            if (response && response.data) {
                setLogs(response.data);
                setPagination(response.pagination);
            }
        } catch (err) {
            toast({
                title: "Erro ao carregar auditoria",
                description: "Não foi possível buscar os logs de auditoria do servidor.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const getActionBadge = (action) => {
        switch (action?.toUpperCase()) {
            case 'INSERT': return <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-none">INCLUSÃO</Badge>;
            case 'UPDATE': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-none">ALTERAÇÃO</Badge>;
            case 'DELETE': return <Badge className="bg-red-100 text-red-700 hover:bg-red-200 border-none">EXCLUSÃO</Badge>;
            case 'LOGIN': return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 border-none">LOGIN</Badge>;
            default: return <Badge variant="secondary">{action}</Badge>;
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-6">
            <Helmet>
                <title>Auditoria do Sistema - PROVATIO</title>
            </Helmet>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-slate-100 rounded-xl text-[#193D6D] shadow-sm border border-slate-200">
                        <ShieldCheck size={28} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-[#193D6D] tracking-tight">Registro de Auditoria</h1>
                        <p className="text-slate-500 font-medium">Histórico completo de ações, alterações e acessos dos usuários.</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => fetchLogs()} disabled={loading} className="border-slate-300">
                        <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Sincronizar
                    </Button>
                    <Button className="bg-[#193D6D] hover:bg-blue-800 text-white shadow-md">
                        <Filter className="w-4 h-4 mr-2" /> Filtros Avançados
                    </Button>
                </div>
            </div>

            <Card className="border-none shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
                <CardHeader className="border-b border-slate-100 pb-6 bg-white">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                            <Input placeholder="Pesquisar por usuário, tabela ou ação..." className="pl-10 bg-slate-50/50 border-slate-200 focus:bg-white transition-all shadow-inner" />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader className="bg-slate-50/50">
                                <TableRow className="hover:bg-transparent border-slate-100">
                                    <TableHead className="w-[180px] font-semibold text-slate-600">Data e Hora</TableHead>
                                    <TableHead className="font-semibold text-slate-600">Usuário</TableHead>
                                    <TableHead className="font-semibold text-slate-600">Ação / Tabela</TableHead>
                                    <TableHead className="font-semibold text-slate-600">Registro</TableHead>
                                    <TableHead className="text-right font-semibold text-slate-600">Detalhes</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 8 }).map((_, i) => (
                                        <TableRow key={i} className="animate-pulse">
                                            <TableCell colSpan={5} className="h-16 bg-slate-50/30"></TableCell>
                                        </TableRow>
                                    ))
                                ) : logs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="h-48 text-center text-slate-400 italic font-medium">
                                            Nenhum registro de auditoria encontrado.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    logs.map((log) => (
                                        <TableRow key={log.id} className="group hover:bg-slate-50 transition-all border-slate-50">
                                            <TableCell className="font-medium text-slate-600">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                                                    <span className="text-xs">{format(new Date(log.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-slate-700 flex items-center gap-1.5 text-sm">
                                                        <User className="w-3.5 h-3.5 text-blue-500" />
                                                        {log.usuario_nome || 'Sistema (Automático)'}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 font-mono tracking-tighter opacity-70">{log.usuario_email || 'n/a'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    {getActionBadge(log.acao)}
                                                    <span className="flex items-center gap-1 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                                                        <Database className="w-3.5 h-3.5 text-slate-400" />
                                                        {log.tabela.replace('_', ' ')}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="text-slate-500 bg-slate-50 font-mono text-[10px] border-slate-200">
                                                    ID: {log.registro_id}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="sm" className="text-[#193D6D] hover:bg-[#193D6D] hover:text-white transition-all group-hover:px-4">
                                                    <FileText className="w-4 h-4 md:mr-2" />
                                                    <span className="hidden md:inline">Ver JSON</span>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {!loading && pagination.total > 0 && (
                <div className="flex items-center justify-between text-sm text-slate-500 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                    <div className="font-medium">
                        Mostrando <span className="text-slate-900">{logs.length}</span> de <span className="text-slate-900">{pagination.total}</span> registros
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={pagination.page <= 1}
                            onClick={() => fetchLogs(pagination.page - 1)}
                            className="border-slate-200 text-[#193D6D] hover:bg-slate-50"
                        >Anterior</Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={pagination.page >= pagination.total_pages}
                            onClick={() => fetchLogs(pagination.page + 1)}
                            className="border-slate-200 text-[#193D6D] hover:bg-slate-50"
                        >Próxima</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
